import { Box, Button } from '@material-ui/core'
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@material-ui/icons'
import Mustache from 'mustache'
import { forwardRef } from 'react'

import { resolveParentCategoryPath, resolveSubCategoryPath } from '@karakuri-web/common/utils'
import {
  CategoryDictionary,
  FaqSettingMinimal,
  FilterCategoryLayout,
  SearchPageContext,
  SearchParams,
} from '@karakuri-web/faq-type'

import { QAHeader, QAPanel } from '../components'
import { useFaqRouting } from '../contexts/FaqRoutingContext'

/**
 * FilterCategoryWidget用の設定インターフェース
 */
export interface FilterCategoryWidgetSettings extends FaqSettingMinimal {
  words?: {
    search?: {
      filterCategoryTitle?: string | null
    } | null
  } | null
}

interface PageProps<D, C, S> {
  data: D
  pageContext: C
  path: string
  location?: { search: string }
  state?: S
}

type Props<T extends FilterCategoryWidgetSettings = FilterCategoryWidgetSettings> = Pick<
  PageProps<{ settingYaml: T }, SearchPageContext, SearchParams>,
  'data' | 'pageContext' | 'path'
> & {
  layout: FilterCategoryLayout
  dictionary: CategoryDictionary
  q?: string[]
}

/**
 * フィルターカテゴリウィジェット
 * 検索結果ページでカテゴリを絞り込むためのウィジェット
 */
export function FilterCategoryWidget<
  T extends FilterCategoryWidgetSettings = FilterCategoryWidgetSettings,
>({ layout, data, pageContext, dictionary, q, path }: Props<T>) {
  const { Link } = useFaqRouting()

  if (!pageContext.category) {
    return null
  }

  const subCategories = pageContext.category.sub || []
  const parent = pageContext.category.parent
  const layoutTitle = layout.title || 'カテゴリ一覧'
  const template =
    data.settingYaml?.words?.search?.filterCategoryTitle || '{{categoryTitle}}の{{layoutTitle}}'

  const title = pageContext.category.title
    ? Mustache.render(template, { categoryTitle: pageContext.category.title, layoutTitle })
    : layoutTitle

  const hasSubcategories = subCategories.length > 0
  return (
    <QAPanel className="faq-filter-category" data-testid="faq-panel-filter-category">
      {hasSubcategories ? <QAHeader title={title} /> : <Box margin={3} />}
      {hasSubcategories && (
        <Box display="flex" flexWrap="wrap" className="faq-filter-category-sub-category-wrapper">
          {subCategories.map(c => {
            const LinkComponent = forwardRef<HTMLAnchorElement>(function LinkComponent(props, ref) {
              return (
                <Link ref={ref} {...props} to={resolveSubCategoryPath(path, c.id)} state={{ q }} />
              )
            })
            return (
              <Button
                key={`${c?.id}`}
                variant="outlined"
                color="secondary"
                className="faq-filter-category-sub-category"
                component={LinkComponent}
                endIcon={<ChevronRightIcon />}
              >
                {c?.title}
              </Button>
            )
          })}
        </Box>
      )}
      {parent && dictionary[parent] && (
        <Button
          startIcon={<ChevronLeftIcon />}
          color="secondary"
          component={forwardRef<HTMLAnchorElement>(function LinkComponent(props, ref) {
            return <Link ref={ref} {...props} to={resolveParentCategoryPath(path)} state={{ q }} />
          })}
        >
          {dictionary[parent].title}
        </Button>
      )}
    </QAPanel>
  )
}
